import { booleanAttribute } from "#ce/utils/props";
import { sizeProps } from "#ce/composables/useSize";
import { typographyProps } from "#ce/composables/useTypography";
import { systemColors } from "#ce/utils/color";

export const LAYOUTS = ["row", "row-reverse", "column", "column-reverse"];
export const ALIGNMENTS = ["start", "center", "end"];
export const COLORS = [...systemColors];

export const props = Object.freeze({
  /**
   * Icon size override on desktop and larger screens.
   *
   * @type {String}
   * @default undefined
   * @storybook-control select
   */
  size: {
    ...sizeProps.size,
  },

  /**
   * Icon size override on small screens.
   *
   * @type {String}
   * @default undefined
   * @storybook-control select
   * @storybook-table-subcategory Small Screen
   */
  sizeSm: {
    ...sizeProps.sizeSm,
  },

  /**
   * Typography token applied to the tile label.
   *
   * @type {String}
   * @default undefined
   * @storybook-control select
   */
  type: {
    ...typographyProps.type,
  },

  /**
   * Label text rendered inside the tile.
   *
   * @type {String}
   * @default undefined
   */
  label: {
    type: String,
    default: undefined,
  },

  /**
   * Icon name to render in the tile.
   *
   * @type {String}
   * @default undefined
   * @storybook-control select
   */
  icon: {
    type: String,
    default: undefined,
  },

  /**
   * Theme colour for the icon glyph. Maps to the `--sp-comp-tile-icon-fill` CSS custom property.
   *
   * @type {String}
   * @default undefined
   * @storybook-control select
   */
  iconColor: {
    type: String,
    default: undefined,
  },

  /**
   * Theme colour for the circular icon background. Maps to the `--sp-comp-tile-icon-bg-color` CSS custom property.
   *
   * @type {String}
   * @default undefined
   * @storybook-control select
   */
  iconBackgroundColor: {
    type: String,
    default: undefined,
  },

  /**
   * Layout direction of tile content (icon + label). "column" stacks the icon above the label; "row" places them side by side; the "-reverse" variants flip the order.
   *
   * @type {String}
   * @default "column"
   * @values row, row-reverse, column, column-reverse
   * @storybook-control radio
   */
  layout: {
    type: String,
    default: "column",
    validator: (value) => LAYOUTS.includes(value),
  },

  /**
   * Layout direction on small screens. Leave undefined to inherit `layout`.
   *
   * @type {String}
   * @default undefined
   * @values row, row-reverse, column, column-reverse
   * @storybook-control radio
   * @storybook-table-subcategory Small Screen
   */
  layoutSm: {
    type: String,
    default: undefined,
    validator: (value) => LAYOUTS.includes(value),
  },

  /**
   * Cross-axis alignment of the tile content and its text. "start" and "end" align both to that edge. "center" centers both, except that a "row" layout on small screens starts the text at the icon's edge.
   *
   * @type {String}
   * @default "center"
   * @values start, center, end
   * @storybook-control radio
   */
  align: {
    type: String,
    default: "center",
    validator: (value) => ALIGNMENTS.includes(value),
  },

  /**
   * Alignment of tile content on small screens. Leave undefined to inherit `align`.
   *
   * @type {String}
   * @default undefined
   * @values start, center, end
   * @storybook-control radio
   * @storybook-table-subcategory Small Screen
   */
  alignSm: {
    type: String,
    default: undefined,
    validator: (value) => ALIGNMENTS.includes(value),
  },

  /**
   * URL for link behaviour. When set, the tile renders as an anchor (`<a>`).
   *
   * @type {String}
   * @default undefined
   */
  href: {
    type: String,
    default: undefined,
  },

  /**
   * Target attribute for the link (e.g. "_blank").
   *
   * @type {String}
   * @default undefined
   */
  target: {
    type: String,
    default: undefined,
  },

  /**
   * Render the tile with card styles (background, border, shadow, padding).
   *
   * @type {Boolean|String}
   * @default false
   */
  card: booleanAttribute(),
});
